import { useState, useCallback } from 'react'

import { useCommentData } from '../../context/CommentContext'
import {
  isInterimExpanded as phaseIsInterimExpanded,
  isResolved as phaseIsResolved,
  selectCardPhase,
} from '../../../hooks/engagement/selectCardPhase'
import type { ReplyFormStatus } from '../../../types'

type UseCommentStateProps = {
  expanded: boolean
  onLocalResolutionChange?: (commentId: string, isResolved: boolean) => void
  isLocallyResolved?: boolean
}

type UseCommentStateReturn = {
  actionsLoading: boolean
  replyStatus: ReplyFormStatus
  isHovered: boolean
  isResolvedByStatus: boolean
  isResolvedLocally: boolean
  isLocallyDismissed: boolean
  isLocallyReplied: boolean
  isInterimExpanded: boolean
  isResolved: boolean
  handleActionsLoadingChange: (loading: boolean) => void
  handleReplyStatusChange: (status: ReplyFormStatus) => void
  handleMouseEnter: () => void
  handleMouseLeave: () => void
  handleDismissed: () => void
  handleUndismissed: () => void
}

/**
 * Custom hook to manage comment state
 * Handles loading states, reply status, hover state, and resolution state
 */
export const useCommentState = ({
  expanded,
  onLocalResolutionChange,
  isLocallyResolved = false,
}: UseCommentStateProps): UseCommentStateReturn => {
  const comment = useCommentData()
  const [actionsLoading, setActionsLoading] = useState(false)
  const [replyStatus, setReplyStatus] = useState<ReplyFormStatus>('idle')
  const [isHovered, setIsHovered] = useState(false)

  // A comment is locally replied if a reply was just published
  const isLocallyReplied = replyStatus === 'published'

  // Server state only: resolved by status from cache or local reply published.
  // Excludes the parent's local-resolution set by design; returned for parity
  // with the legacy shape.
  const isResolvedByStatus =
    comment.status === 'dismissed' ||
    comment.status === 'replied' ||
    isLocallyReplied

  // Local resolution state (separate from server state)
  const isResolvedLocally = isLocallyReplied || isLocallyResolved

  // Distinguish between locally dismissed vs locally replied
  // A comment is locally dismissed if it's in the parent's resolved set but not from a reply
  const isLocallyDismissed = isLocallyResolved && !isLocallyReplied

  // Resolution phase shared with `useMentionState` (see `selectCardPhase`). A
  // published reply resolves the card even when no parent owns the local
  // resolution set, so it is folded into the selector's `isLocallyResolved`
  // input alongside the parent-owned flag (`isResolvedLocally`) — reproducing
  // this hook's prior `status || reply || locallyResolved` derivation exactly.
  const phase = selectCardPhase({
    status: comment.status,
    expanded,
    isLocallyResolved: isResolvedLocally,
    isLocallyReplied,
  })
  const isInterimExpanded = phaseIsInterimExpanded(phase)
  const isResolved = phaseIsResolved(phase)

  const handleActionsLoadingChange = useCallback((loading: boolean) => {
    setActionsLoading(loading)
  }, [])

  const handleReplyStatusChange = useCallback(
    (status: ReplyFormStatus) => {
      const normalizedStatus = status === 'error' ? 'idle' : status
      setReplyStatus(normalizedStatus)

      // Mark as locally resolved only when published successfully
      // On error or other states, remove from locally resolved set
      const replyIsResolved = normalizedStatus === 'published'
      onLocalResolutionChange?.(comment.id, replyIsResolved)

      // Reset hover state when reply is processing
      if (status === 'processing') {
        setIsHovered(false)
      }
    },
    [comment.id, onLocalResolutionChange],
  )

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)
  }, [])

  const handleDismissed = useCallback(() => {
    // Notify parent that comment was dismissed
    onLocalResolutionChange?.(comment.id, true)
  }, [comment.id, onLocalResolutionChange])

  const handleUndismissed = useCallback(() => {
    // Notify parent that comment was undismissed
    onLocalResolutionChange?.(comment.id, false)
  }, [comment.id, onLocalResolutionChange])

  return {
    actionsLoading,
    replyStatus,
    isHovered,
    isResolvedByStatus,
    isResolvedLocally,
    isLocallyDismissed,
    isLocallyReplied,
    isInterimExpanded,
    isResolved,
    handleActionsLoadingChange,
    handleReplyStatusChange,
    handleMouseEnter,
    handleMouseLeave,
    handleDismissed,
    handleUndismissed,
  }
}
